import React, { createContext, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { useAuth } from "context/AuthContext";
import { useStorefrontTenant } from "context/StorefrontTenantContext";
import {
  getStorefrontConfig,
  resolveStorefrontCompanyKey,
  resolveStorefrontLocale,
} from "lib/storefront";
import {
  currencyForCompany,
  readCompanyProfile,
} from "lib/companyProfile";

const CurrencyContext = createContext(null);

function normalizeCurrencyCode(value, fallback = "USD") {
  const currency = String(value || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(currency) ? currency : fallback;
}

export function CurrencyProvider({ children }) {
  const { activeCompany } = useAuth();
  const { tenant, tenantKey } = useStorefrontTenant();
  const storefront = getStorefrontConfig();
  const companyKey = resolveStorefrontCompanyKey({
    tenantKey,
    activeCompany: tenantKey ? null : activeCompany,
  });
  const profile = readCompanyProfile(companyKey);
  const currency = normalizeCurrencyCode(
    profile?.baseCurrency ||
      tenant?.baseCurrency ||
      storefront?.currency ||
      currencyForCompany(companyKey, tenantKey === "mx" ? "MXN" : "USD"),
  );
  const locale =
    resolveStorefrontLocale({ tenant, tenantKey, companyKey }) ||
    storefront?.locale ||
    "en-US";
  const showMXN = currency === "MXN";
  const mxnRate = 1; // Company-fixed currency; no global conversions
  const setShowMXN = () => {}; // kept for backwards-compat (UI no longer toggles)
  const setMxnRate = () => {}; // kept for backwards-compat (rate no longer editable)

  // Format an amount for display according to the active company.
  const formatUSDForDisplay = (amountUSD) => {
    const num = Number(amountUSD || 0);
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(
      num || 0,
    );
  };

  // No conversion. Inputs are always stored in the company's currency.
  const parseDisplayToUSD = (displayAmount) => {
    const num = Number(displayAmount || 0);
    return num;
  };

  // Format an amount in its original currency.
  // Canonical value is always `amount` (company currency); `amountInUSD` is only a legacy mirror fallback.
  const formatByOriginalCurrency = (
    amount,
    amountCurrency = "USD",
    amountInUSD = null,
  ) => {
    const cur = normalizeCurrencyCode(amountCurrency, showMXN ? "MXN" : "USD");
    const amountNumber = Number(amount);
    const usdMirrorNumber = Number(amountInUSD);
    const value = Number.isFinite(amountNumber)
      ? amountNumber
      : Number.isFinite(usdMirrorNumber)
        ? usdMirrorNumber
        : 0;
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: cur,
    }).format(value || 0);
  };

  const value = useMemo(
    () => ({
      currency,
      locale,
      showMXN,
      setShowMXN,
      mxnRate,
      setMxnRate,
      formatUSDForDisplay,
      parseDisplayToUSD,
      formatByOriginalCurrency,
    }),
    [currency, showMXN, mxnRate, locale],
  );

  return (
    <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within a CurrencyProvider");
  return ctx;
}

CurrencyProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
